// Copyright 2023, Durable
import React, { RefObject, forwardRef, useMemo, useRef } from "react";
import Slot, { slotIsPopulated } from "./Slot";
import { twMerge as mergeTailwindClasses } from "tailwind-merge";
import { onClickwithKeyboard } from "../../../helpers";

/**
 * A material card to separate its contents visually from its background.
 * Follows basic specification from
 * https://m3.material.io/components/cards/overview
 */
const Card = forwardRef(function (
  {
    children,
    id,
    className,
    tabIndex = 0,
    onClick,
    onMouseEnter,
    onMouseLeave
  }: {
    children?;
    id?: string;
    className?: string;
    tabIndex?: number;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  },
  ref?: RefObject<HTMLElement>
) {
  // If no ref is passed in, still populate it
  const localRef = useRef();
  const cardRef = ref || localRef;

  return (
    <section
      className={mergeTailwindClasses(
        "bg-coolGrey-1000 dark:bg-coolGrey-100 rounded-xl overflow-auto",
        className
      )}
      id={id}
      {...onClickwithKeyboard(onClick, tabIndex)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={cardRef}
    >
      <div>
        <header id="card-header" className="flex w-full">
          {slotIsPopulated("cardHeader", children) && (
            <div id="card-header" className="pt-4 pl-4 w-full">
              <Slot name="cardHeader" elements={children} />
            </div>
          )}
          {slotIsPopulated("topRight", children) && (
            <div
              id="top-right"
              className="flex flex-grow justify-end pr-4 pt-4"
            >
              <Slot name="topRight" elements={children} />
            </div>
          )}
        </header>
        {slotIsPopulated("cardBody", children) && (
          <div id="card-body" className="p-4 w-full">
            <Slot name="cardBody" elements={children} />
          </div>
        )}
      </div>
      {useMemo(
        () => (
          <Slot elements={children} />
        ),
        [children]
      )}
    </section>
  );
});
Card.displayName = "Card";

export default Card;
